import { h, type Component } from 'vue';
import type { VueBridge } from './vue-app';

/** Реестр Vue компонентов */
const vueComponentsRegistry: Map<string, Component> = new Map();

/**
 * Регистрация Vue компонента для использования в Angular
 * 
 * @param name имя, по которому можно будет обратится в Angular
 * @param component vue - компонент
 */
export function registerVueComponent(name: string, component: Component): void {
  vueComponentsRegistry.set(name, component);
}

/**
 * Зарегистритрует Angular директиву, для рендеринга Vue компонентов
 * 
 * @param angular 
 * @param name 
 */
export function registerVueComponentBridgeDirective(angular: angular.IAngularStatic, vueBridge: VueBridge, name: string = 'vueComponent') {
  angular.module('app').directive(name, ['$parse', '$timeout', function ($parse: ng.IParseService, $timeout: ng.ITimeoutService) {
    return {
      restrict: 'E',
      scope: {
        name: '@',           // Имя Vue компонента
        props: '<',          // Props для Vue компонента
        events: '<',         // События { eventName: handler }
      },

      transclude: true,      // Для слотов

      link: function (
        scope: ng.IScope & { name: string; props: any; events: any },
        element: JQuery,
        attrs: ng.IAttributes,
        ctrl: any,
        transclude: ng.ITranscludeFunction
      ) {
        const container = element[0] as HTMLElement;
        let mounted: ReturnType<typeof vueBridge.mount> | null = null;
        let slotContent: HTMLElement | null = null;

        const vueComponent = vueComponentsRegistry.get(scope.name);
        if (!vueComponent) {
          console.error(`[vueComponent] Component "${scope.name}" not found in registry`);
          return;
        }

        /**
         * Обработка transcluded content для слотов
         * 
         * @returns 
         */
        function getSlots(): Record<string, () => any> {
          const slots: Record<string, () => any> = {};

          if (transclude) {
            transclude((clone: JQuery) => {
              if (clone && clone.length) {
                const domElements = clone.toArray();
                let isMounted = false;

                slots.default = () => {
                  return h('div', {
                    class: 'vue-slot-container',
                    ref: (el: HTMLElement | null) => {
                      if (el && !isMounted) {
                        el.replaceChildren(...domElements);
                        isMounted = true;
                      }
                    }
                  });
                };
              }
            });
          }

          return slots;
        }

        /**
         * Преобразование Angular событий в Vue формат
         * 
         * @param events 
         * @returns 
         */
        function wrapEvents(events: Record<string, Function> = {}): Record<string, Function> {
          const wrapped: Record<string, Function> = {};

          Object.entries(events).forEach(([event, handler]) => {
            wrapped[event] = (...args: any[]) => {
              $timeout(() => { // Вызываем в контексте Angular digest
                handler(...args);
              });
            };
          });

          return wrapped;
        }

        /**
         * Монтирование Vue компонента
         */
        function mountComponent(): void {
          if (mounted) {
            mounted.unmount();
          }

          const props = scope.props || {};
          const events = wrapEvents(scope.events);
          const slots = getSlots();

          mounted = vueBridge.mount(container, vueComponent, props, slots, events);
        }

        // Watch props изменений
        scope.$watch('props', (newProps, oldProps) => {
          if (mounted && newProps !== oldProps) {
            mounted.updateProps(newProps || {});
          }
        }, true);

        // Watch events изменений
        scope.$watch('events', () => {
          // Перемонтируем при изменении events
          // TODO: Надо тестить на сколько это рабочий вариант
          mountComponent();
        }, true);

        // Первичное монтирование
        $timeout(() => {
          mountComponent();
        });

        // Cleanup при уничтожении Angular scope
        scope.$on('$destroy', () => {
          if (mounted) {
            mounted.unmount();
            mounted = null;
          }

          if (slotContent && slotContent.parentNode) {
            slotContent.parentNode.removeChild(slotContent);
          }
        });
      }
    };
  }]);
}
